import type { components } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type GroupErasure = components['schemas']['GroupErasureResource']
export type ErasurePlan = components['schemas']['ErasurePlanResource']

const erasureKey = (groupId: string) => ['platform', 'groups', groupId, 'erasure'] as const

/** The group's last erasure dry run (f418 D6); `plan` is null before the first. */
export function useGroupErasure(groupId: string, enabled: boolean) {
  return useQuery({
    queryKey: erasureKey(groupId),
    enabled,
    queryFn: async (): Promise<GroupErasure> => {
      const result = await api.GET('/platform/groups/{group}/erasure', {
        params: { path: { group: groupId } },
      })
      if (!result.data) throw new Error('GET /platform/groups/{group}/erasure returned no body')
      return result.data.data
    },
  })
}

/** Runs a dry run: the API counts, stores the plan, and deletes nothing. */
export function useRunErasureDryRun(groupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<GroupErasure> => {
      const result = await api.POST('/platform/groups/{group}/erasure/plan', {
        params: { path: { group: groupId } },
      })
      if (!result.data)
        throw new Error('POST /platform/groups/{group}/erasure/plan returned no body')
      return result.data.data
    },
    onSuccess: (erasure) => {
      queryClient.setQueryData(erasureKey(groupId), erasure)
    },
  })
}
